import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import ModalSaveMode from "@/widgets/modalSaveMode/ui/ModalSaveMode/ModalSaveMode";
import { useGetSavedPointsAllQuery } from "@/features/auth/api/authAPI";
import { useCurrentOrganization } from "@/Hooks";

const SavedModeVideoView = () => {
  const currenOrganization = useCurrentOrganization();
  const { zoom, sortkey, userFilter } = useOutletContext();
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [allResults, setAllResults] = useState<any[]>([]);

  const {
    data: savedPoints,
    refetch: refetchSavedPoints,
    isLoading,
    isFetching,
  } = useGetSavedPointsAllQuery(
    {
      organizationUuid: currenOrganization?.uuid,
      order: sortkey,
      user: userFilter,
      page,
      pageSize: PAGE_SIZE,
    },
    {
      skip: !currenOrganization,
    },
  );

  useEffect(() => {
    if (savedPoints?.results) {
      if (page === 1) {
        setAllResults(savedPoints.results);
      } else {
        // Append new results, avoiding duplicates if a page is somehow fetched again
        const newItems = savedPoints.results.filter(
          (newItem) =>
            !allResults.some((existingItem) => existingItem.id === newItem.id),
        );
        if (newItems.length > 0) {
          setAllResults((prev) => [...prev, ...newItems]);
        }
      }
    }
  }, [savedPoints?.results, page]);

  const handleLocalDelete = useCallback(
    (deletedItemIds: number[]) => {
      setAllResults((prevResults) =>
        prevResults.filter((item) => !deletedItemIds.includes(item.id)),
      );

      // After local deletion, refetch the *current* page.
      // This helps to:
      // 1. Fill up the page if items were deleted from it and more items are available on the backend for this page.
      // 2. Get an updated 'next' link from the API if the total count changed.
      // This is a compromise to avoid a full reset to page 1.
      refetchSavedPoints();
    },
    [refetchSavedPoints],
  );

  const handleItemsAdded = useCallback((newItems: any[]) => {
    setAllResults((prevResults) => {
      // Check for duplicates and add only new items
      const existingIds = new Set(prevResults.map((item) => item.id));
      const uniqueNewItems = newItems.filter(
        (item) => !existingIds.has(item.id),
      );

      if (uniqueNewItems.length > 0) {
        // Add new items to the beginning of the array (most recent first)
        return [...uniqueNewItems, ...prevResults];
      }
      return prevResults;
    });
  }, []);

  const combinedData = {
    ...(savedPoints || {}), // Spread current API response, but override results, count, next
    results: allResults,
    count: allResults.length, // This is the count of items currently loaded client-side
    // Crude 'next' logic: if the last API fetch had a 'next' and we still have enough items to potentially show more
    // A more robust 'next' would involve checking if `allResults.length < trueTotalCountFromApiIfAvailable`
    next:
      savedPoints?.next &&
      allResults.length > 0 &&
      allResults.length === page * PAGE_SIZE
        ? savedPoints.next
        : null,
    // If the last API call indicated no more pages, then 'next' should be null.
    // If after deletion, allResults.length < page * PAGE_SIZE, it implies we might have deleted from the current last page.
    // The refetchSavedPoints() in handleLocalDelete will help update this 'next' from the API.
  };

  const [activeIndex, setActiveIndex] = useState(0);
  const photoGroups = useRef<HTMLDivElement>(null);

  const applyZoom = (zoom: number) => {
    const photoCardListElement = photoGroups.current;

    if (photoCardListElement) {
      photoCardListElement.style.setProperty("--zoom-level", zoom.toString());
    }
  };

  useEffect(() => {
    applyZoom(zoom);
  }, [zoom]);

  return (
    <div>
      <ModalSaveMode
        dataSavedPoint={combinedData}
        activeIndex={activeIndex}
        refetchSavedPoints={refetchSavedPoints}
        viewType="video"
        page={page}
        setPage={setPage}
        isLoading={isLoading || isFetching}
        isFetching={isFetching}
        pageSize={PAGE_SIZE}
        onItemsDeleted={handleLocalDelete}
        onItemsAdded={handleItemsAdded}
      />
    </div>
  );
};

export { SavedModeVideoView };
